import { el, enc, fq, nm, pullOf, qtyText, type CardOrder } from './card';

/**
 * The Zebra tickets (decision 180), as the Sheets' Prep.gs printed them: one 4 × 6 label per order for whoever fills it
 * (the customer big, the invoice and date under it, a box to tick on every line, "Pulled / Checked" at the foot), and
 * TOTALES (every order added together) on 4 × 6 labels too. An order longer than a label runs on to the next, marked
 * (cont.) and 1/2, 2/2. It measures what it lays out, so it works on a container in the page (off screen).
 */
export interface TicketOrder extends CardOrder {
  invoiceNo: number;
  date: string;
}
export interface TicketWords {
  totals: string;
  cont: string;
  pulled: string;
  checked: string;
  /** "{n} lines · {q} qty", and "1 line · {q} qty" */
  linesQty: string;
  lineQty: string;
  /** "{n} orders" */
  orders: string;
  /** "{n} items · {q}", and "1 item · {q}" */
  items: string;
  item: string;
}

/** One label: the title, the line under it, the rows, and the foot. */
function zPage(out: HTMLElement, title: string, sub: string, words: TicketWords): HTMLElement {
  const pg = el(
    `<div class="pg z"><div class="zh">${title}</div><div class="zs">${sub}</div><div class="in"></div><div class="zf"><span>${enc(words.pulled)} ________</span><span>${enc(words.checked)} ________</span><b class="pn"></b></div></div>`,
  );
  out.appendChild(pg);
  return pg;
}

/** Pour rows into labels one at a time; a row that runs past a label's foot starts the next label. */
function pour(rows: HTMLElement[], nextBox: () => HTMLElement) {
  let box = nextBox();
  let inBox = 0;
  for (const r of rows) {
    box.appendChild(r);
    inBox++;
    if (inBox > 1 && r.getBoundingClientRect().bottom > box.getBoundingClientRect().bottom - 2) {
      r.remove();
      box = nextBox();
      box.appendChild(r);
      inBox = 1;
    }
  }
}

const row = (qty: number, name: string, tag: string | null, note: string | null, orders = 0) =>
  el(
    `<div class="zr"><span class="bx"></span><span class="q">${fq(qty)}</span><span class="it">${nm(name, tag)}${note ? `<span class="ds">${enc(note)}</span>` : ''}</span>${orders > 1 ? `<span class="k">×${orders}</span>` : ''}</div>`,
  );
const numberPages = (mine: HTMLElement[]) => mine.forEach((pg, i) => (pg.querySelector('.pn')!.textContent = mine.length > 1 ? `${i + 1}/${mine.length}` : ''));

/** One ticket (or more for a long order) per order, in the list's order. Returns how many labels it takes. */
export function buildTickets(out: HTMLElement, orders: TicketOrder[], words: TicketWords, dateText: (iso: string) => string): number {
  out.innerHTML = '';
  let labels = 0;
  for (const t of orders) {
    const q = qtyText(t.lines);
    const mine: HTMLElement[] = [];
    const sub = `<span>#${enc(t.invoiceNo)} · ${enc(dateText(t.date))}</span><span>${enc((t.lines.length === 1 ? words.lineQty : words.linesQty).replace('{n}', String(t.lines.length)).replace('{q}', q))}</span>`;
    pour(
      t.lines.map((l) => row(l.qty, l.name, l.tag, l.note)),
      () => {
        const pg = zPage(out, `${enc(t.customer)}${mine.length ? ` <small>${enc(words.cont)}</small>` : ''}`, sub, words);
        mine.push(pg);
        return pg.querySelector<HTMLElement>('.in')!;
      },
    );
    numberPages(mine);
    labels += mine.length;
  }
  return labels;
}

/** TOTALES on labels: every order added together, item by item, ×N for how many orders take it. */
export function buildPull(out: HTMLElement, orders: CardOrder[], words: TicketWords, rangeText: string): number {
  out.innerHTML = '';
  const pull = pullOf(orders);
  const total = qtyText(pull);
  const mine: HTMLElement[] = [];
  const sub = `<span>${enc(rangeText)} · ${enc(words.orders.replace('{n}', String(orders.length)))}</span><span>${enc((pull.length === 1 ? words.item : words.items).replace('{n}', String(pull.length)).replace('{q}', total))}</span>`;
  pour(
    pull.map((p) => row(p.qty, p.name, p.tag, null, p.orders)),
    () => {
      const pg = zPage(out, `${enc(words.totals)}${mine.length ? ` <small>${enc(words.cont)}</small>` : ''}`, sub, words);
      mine.push(pg);
      return pg.querySelector<HTMLElement>('.in')!;
    },
  );
  numberPages(mine);
  return mine.length;
}
